// The Shop lights' reads and presses, shared by the desktop Doors page (pages/ShopLightsSection.jsx) and the phone
// Cameras screen (mobile/screens/cameras/PhoneLights.jsx). Layout stays with each screen; `ui` is the screen's own
// dialogs (desktopUi / phoneUi), so a phone never calls window.confirm. Same shape as useDeadbolts.js.

import { useCallback, useEffect, useRef, useState } from 'react';
import { needsAdminPin } from '../../utils/securityApi';
import { usePolling } from '../../components/cameras/useCameraData';
import {
  LIGHTS_FOLLOW_MS,
  LIGHTS_POLL_MS,
  anyPending,
  fetchLights,
  offConfirmText,
  pressedText,
  sendLightCommand,
  setAutoOff,
} from './lights';

/** The light relays, read every 30 s, every 5 s while a press waits for a relay to collect it. */
export function useShopLights({ active = true, onRefused }) {
  const [data, setData] = useState(null);
  const [readFailed, setReadFailed] = useState(false);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; };
  }, []);

  const load = useCallback(async () => {
    const r = await fetchLights();
    if (!live.current) return;
    if (needsAdminPin(r)) {
      if (onRefused) onRefused(r.message);
      return;
    }
    const good = Boolean(r.ok && r.body);
    setReadFailed(!good);
    if (good) setData(r.body);
  }, [onRefused]);

  usePolling(load, anyPending(data) ? LIGHTS_FOLLOW_MS : LIGHTS_POLL_MS, active);

  return { data, readFailed, load };
}

/** One area's On / Off (Off asks first) and its auto-off switch. */
export function useLightControls({ relay, ui, onChanged, onRefused }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const press = async (action) => {
    if (action === 'off' && !(await ui.confirm(offConfirmText(relay)))) return;
    setBusy(true);
    setMsg(null);
    const r = await sendLightCommand(relay.relay_id, action);
    setBusy(false);
    if (needsAdminPin(r)) {
      if (onRefused) onRefused(r.message);
      return;
    }
    if (!r.ok) {
      setMsg({ kind: 'danger', text: r.message || 'JobQ refused that.' });
      return;
    }
    setMsg({ kind: 'info', text: pressedText(action) });
    onChanged();
  };

  const toggleAuto = async () => {
    setBusy(true);
    setMsg(null);
    const r = await setAutoOff(relay.relay_id, !relay.auto_off);
    setBusy(false);
    if (needsAdminPin(r)) {
      if (onRefused) onRefused(r.message);
      return;
    }
    if (!r.ok) {
      setMsg({ kind: 'danger', text: r.message || 'JobQ refused that.' });
      return;
    }
    onChanged();
  };

  return { busy, msg, press, toggleAuto };
}
